import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Clock,
  MapPin,
  Calendar,
  Loader2,
  Save,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  ChevronDown,
  Lock,
  Search,
  AlertTriangle,
  UserX,
  History,
} from 'lucide-react';
import {
  useAttendanceForSession,
  useMarkAttendance,
} from '../../hooks/useProfessor';
import type {
  AttendanceRecord,
  MarkAttendanceDto,
  ScheduledSessionInfo,
} from '@/types/professor.types';
import { AttendanceStatus } from '@/types/grade.types';

interface AttendanceModalProps {
  session: ScheduledSessionInfo;
  groupeId: string;
  moduleElementId: string;
  onClose: () => void;
  onSaved: () => void;
}

export const AttendanceModal: React.FC<AttendanceModalProps> = ({
  session,
  groupeId,
  moduleElementId,
  onClose,
  onSaved,
}) => {
  const [attendanceEdits, setAttendanceEdits] = useState<Record<string, { status: AttendanceStatus; lateMinutes?: number }>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLateInput, setShowLateInput] = useState<string | null>(null);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [initializedForSession, setInitializedForSession] = useState<string | null>(null);

  // Use the session.id (scheduleSessionId) to fetch and track attendance
  const scheduleSessionId = session.id;

  // Get the date in YYYY-MM-DD format for display purposes only
  const sessionDate = typeof session.date === 'string' && session.date.includes('T')
    ? session.date.split('T')[0]
    : new Date(session.date).toISOString().split('T')[0];

  // Fetch attendance for session using scheduleSessionId
  const {
    data: sessionData,
    isLoading: sessionLoading,
    refetch: refetchSessionData
  } = useAttendanceForSession(groupeId, moduleElementId, scheduleSessionId);

  const markAttendanceMutation = useMarkAttendance();

  // Initialize edits from existing attendance data when data loads
  // Use scheduleSessionId as the key to track initialization
  useEffect(() => {
    if (sessionData && initializedForSession !== scheduleSessionId) {
      const initialEdits: Record<string, { status: AttendanceStatus; lateMinutes?: number }> = {};

      sessionData.records.forEach((record) => {
        // A former member's row is history: displayed, never edited or posted
        if (record.isFormerMember) return;
        if (record.status && record.status !== 'NOT_MARKED') {
          // Already marked (session being corrected): keep what was recorded.
          initialEdits[record.studentId] = {
            status: record.status as AttendanceStatus,
            lateMinutes: record.lateMinutes ?? undefined,
          };
        } else {
          // Never marked: everyone starts PRESENT so the professor only taps
          // the exceptions. Nothing is written until "Enregistrer" is clicked.
          initialEdits[record.studentId] = { status: AttendanceStatus.PRESENT };
        }
      });

      // Set the edits (even if empty, to clear previous session's edits)
      setAttendanceEdits(initialEdits);
      setSearchQuery('');
      setShowLateInput(null);
      setInitializedForSession(scheduleSessionId);
    }
  }, [sessionData, scheduleSessionId, initializedForSession]);

  // Format date display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Handle attendance change
  const handleAttendanceChange = (studentId: string, status: AttendanceStatus, lateMinutes?: number) => {
    if (sessionData?.records.some((r) => r.studentId === studentId && r.isFormerMember)) return;
    setAttendanceEdits((prev) => ({
      ...prev,
      [studentId]: { status, lateMinutes },
    }));

    // Show late input if LATE status
    if (status === AttendanceStatus.LATE) {
      setShowLateInput(studentId);
    } else if (showLateInput === studentId) {
      setShowLateInput(null);
    }
  };

  // Check if there are unmarked students
  const getUnmarkedCount = useCallback(() => {
    if (!sessionData) return 0;
    return sessionData.records.filter((record) => {
      const edit = attendanceEdits[record.studentId];
      const status = edit?.status || record.status;
      return !status || status === 'NOT_MARKED';
    }).length;
  }, [sessionData, attendanceEdits]);

  // Handle save button click - check for incomplete first
  const handleSaveClick = () => {
    const unmarkedCount = getUnmarkedCount();
    if (unmarkedCount > 0) {
      setShowIncompleteWarning(true);
    } else {
      handleSaveAttendance();
    }
  };

  // Mark remaining as absent and save
  const handleMarkRemainingAbsentAndSave = () => {
    if (!sessionData) return;

    const newEdits = { ...attendanceEdits };
    sessionData.records.forEach((record) => {
      if (record.isFormerMember) return;
      const currentStatus = attendanceEdits[record.studentId]?.status || record.status;
      if (!currentStatus || currentStatus === 'NOT_MARKED') {
        newEdits[record.studentId] = { status: AttendanceStatus.ABSENT_UNEXCUSED };
      }
    });
    setAttendanceEdits(newEdits);
    setShowIncompleteWarning(false);

    // Save after a short delay to let state update
    setTimeout(() => {
      handleSaveAttendanceWithEdits(newEdits);
    }, 100);
  };

  // Save attendance with specific edits
  const handleSaveAttendanceWithEdits = async (edits: Record<string, { status: AttendanceStatus; lateMinutes?: number }>) => {
    if (!sessionData || !session.canEdit) return;

    setIsSaving(true);

    // Former members are read-only history: their rows are never posted
    const attendanceRecords = sessionData.records
      .filter((record) => !record.isFormerMember)
      .map((record) => {
        const edit = edits[record.studentId];
        return {
          studentId: record.studentId,
          status: edit?.status || (record.status as AttendanceStatus) || AttendanceStatus.ABSENT_UNEXCUSED,
          lateMinutes: edit?.lateMinutes,
        };
      });

    const dto: MarkAttendanceDto = {
      sessionDate,
      scheduleSessionId: scheduleSessionId,
      attendanceRecords,
    };

    try {
      await markAttendanceMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto,
      });
      setAttendanceEdits({});
      refetchSessionData();
      onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to save attendance:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Save attendance
  const handleSaveAttendance = async () => {
    if (!sessionData || !session.canEdit) return;

    setIsSaving(true);

    // Former members are read-only history: their rows are never posted
    const attendanceRecords = sessionData.records
      .filter((record) => !record.isFormerMember)
      .map((record) => {
        const edit = attendanceEdits[record.studentId];
        return {
          studentId: record.studentId,
          status: edit?.status || (record.status as AttendanceStatus) || AttendanceStatus.PRESENT,
          lateMinutes: edit?.lateMinutes,
        };
      });

    const dto: MarkAttendanceDto = {
      sessionDate,
      scheduleSessionId: scheduleSessionId,
      attendanceRecords,
    };

    try {
      await markAttendanceMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto,
      });
      setAttendanceEdits({});
      refetchSessionData();
      onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to save attendance:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Mark all with same status
  const handleMarkAll = (status: AttendanceStatus) => {
    if (!sessionData || !session.canEdit) return;
    const newEdits: Record<string, { status: AttendanceStatus; lateMinutes?: number }> = {};
    sessionData.records.forEach((record) => {
      if (record.isFormerMember) return;
      newEdits[record.studentId] = { status };
    });
    setAttendanceEdits(newEdits);
  };

  // Mark remaining (unmarked) students
  const handleMarkRemaining = (status: AttendanceStatus) => {
    if (!sessionData || !session.canEdit) return;
    const newEdits = { ...attendanceEdits };
    sessionData.records.forEach((record) => {
      if (record.isFormerMember) return;
      const currentStatus = attendanceEdits[record.studentId]?.status || record.status;
      if (currentStatus === 'NOT_MARKED' || !currentStatus) {
        newEdits[record.studentId] = { status };
      }
    });
    setAttendanceEdits(newEdits);
  };

  // Get student initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
      'bg-rose-500',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PRESENT':
        return {
          bg: 'bg-emerald-100',
          text: 'text-emerald-700',
          label: 'Présent',
          icon: <CheckCircle className="h-4 w-4" />,
        };
      case 'ABSENT_UNEXCUSED':
        return {
          bg: 'bg-red-100',
          text: 'text-red-700',
          label: 'Absent',
          icon: <XCircle className="h-4 w-4" />,
        };
      case 'ABSENT_EXCUSED':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-700',
          label: 'Excusé',
          icon: <AlertCircle className="h-4 w-4" />,
        };
      case 'LATE':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-700',
          label: 'Retard',
          icon: <Clock className="h-4 w-4" />,
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-500',
          label: 'Non marqué',
          icon: null,
        };
    }
  };

  // Filter students by search
  const filteredRecords = sessionData?.records.filter((record) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      record.studentName.toLowerCase().includes(query) ||
      record.studentCode.toLowerCase().includes(query)
    );
  }) || [];

  // Count statuses
  const getCounts = () => {
    if (!sessionData) return { present: 0, absent: 0, excused: 0, late: 0, notMarked: 0 };

    let present = 0, absent = 0, excused = 0, late = 0, notMarked = 0;

    sessionData.records.forEach((record) => {
      const edit = attendanceEdits[record.studentId];
      const status = edit?.status || record.status;

      switch (status) {
        case 'PRESENT': present++; break;
        case 'ABSENT_UNEXCUSED': absent++; break;
        case 'ABSENT_EXCUSED': excused++; break;
        case 'LATE': late++; break;
        default: notMarked++;
      }
    });

    return { present, absent, excused, late, notMarked };
  };

  const counts = getCounts();

  // Check if there are actual changes (not just initialization)
  const hasRealChanges = () => {
    if (!sessionData) return false;

    // Check if any edit differs from original record
    for (const record of sessionData.records) {
      const edit = attendanceEdits[record.studentId];
      if (edit) {
        // If the record was NOT_MARKED and now has a status, that's a change
        if (!record.status || record.status === 'NOT_MARKED') {
          return true;
        }
        // If the status changed from original
        if (edit.status !== record.status) {
          return true;
        }
        // If late minutes changed
        if (edit.status === 'LATE' && edit.lateMinutes !== record.lateMinutes) {
          return true;
        }
      }
    }
    return false;
  };

  const hasChanges = hasRealChanges();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="px-6 py-5 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Feuille de Présence
                  </h2>
                  <p className="text-blue-100 text-sm mt-0.5">
                    {formatDate(session.date)}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Session Info */}
            <div className="flex items-center gap-6 mt-4 text-sm text-blue-100">
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {session.startTime} - {session.endTime}
              </span>
              {session.roomName && (
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {session.roomName}
                </span>
              )}
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {sessionData?.records.length || 0} étudiants
              </span>
            </div>
          </div>

          {/* Quick Stats & Actions Bar */}
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            {/* Quick Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">
                <CheckCircle className="h-4 w-4" />
                {counts.present}
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium">
                <XCircle className="h-4 w-4" />
                {counts.absent}
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                <AlertCircle className="h-4 w-4" />
                {counts.excused}
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium">
                <Clock className="h-4 w-4" />
                {counts.late}
              </div>
              {counts.notMarked > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium">
                  {counts.notMarked} non marqués
                </div>
              )}
            </div>

            {/* Quick Actions */}
            {session.canEdit && sessionData && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleMarkAll(AttendanceStatus.PRESENT)}
                  className="px-4 py-2 text-sm bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors font-medium border border-emerald-200"
                >
                  Tous présents
                </button>
                <button
                  onClick={() => handleMarkRemaining(AttendanceStatus.ABSENT_UNEXCUSED)}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium border border-gray-200"
                >
                  Marquer restants absents
                </button>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div className="px-6 py-3 border-t border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un étudiant par nom ou code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Content - Table */}
        <div className="flex-1 overflow-auto">
          {sessionLoading || !sessionData ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" />
                <p className="mt-3 text-sm text-gray-500">Chargement des étudiants...</p>
              </div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                    #
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                    Code
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">
                    Statut actuel
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRecords.map((record: AttendanceRecord, index: number) => {
                  const edit = attendanceEdits[record.studentId];
                  const currentStatus = edit?.status || record.status;
                  const currentLateMinutes = edit?.lateMinutes ?? record.lateMinutes ?? 15;
                  const statusBadge = getStatusBadge(currentStatus);
                  const isModified = !!edit;

                  return (
                    <tr
                      key={record.studentId}
                      className={`hover:bg-gray-50 transition-colors ${isModified ? 'bg-blue-50/30' : ''}`}
                    >
                      {/* Index */}
                      <td className="px-6 py-4 text-sm text-gray-400 font-medium">
                        {index + 1}
                      </td>

                      {/* Student Info */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${getAvatarColor(record.studentName)} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
                            {getInitials(record.studentName)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {record.studentName}
                              {record.isFormerMember && (
                                <span
                                  className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                                  title="Cet étudiant a quitté le groupe ; sa présence à cette séance est conservée en lecture seule"
                                >
                                  <History className="h-3 w-3" />
                                  Ancien membre
                                </span>
                              )}
                              {isModified && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                  Modifié
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Student Code */}
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                        {record.studentCode}
                      </td>

                      {/* Current Status */}
                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${statusBadge.bg} ${statusBadge.text} text-sm font-medium rounded-full`}>
                            {statusBadge.icon}
                            {statusBadge.label}
                            {currentStatus === 'LATE' && (
                              <span className="text-xs">({currentLateMinutes}min)</span>
                            )}
                          </span>
                        </div>
                      </td>

                      {/* Action Buttons */}
                      <td className="px-6 py-4">
                        {record.isFormerMember ? (
                          <div className="flex items-center justify-center gap-1.5 text-xs text-amber-700">
                            <Lock className="h-3.5 w-3.5" />
                            Lecture seule
                          </div>
                        ) : session.canEdit ? (
                          <div className="flex items-center justify-center gap-2">
                            {/* Present Button */}
                            <button
                              onClick={() => handleAttendanceChange(record.studentId, AttendanceStatus.PRESENT)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                                currentStatus === 'PRESENT'
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-300 shadow-sm'
                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200'
                              }`}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>

                            {/* Absent Button */}
                            <button
                              onClick={() => handleAttendanceChange(record.studentId, AttendanceStatus.ABSENT_UNEXCUSED)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                                currentStatus === 'ABSENT_UNEXCUSED'
                                  ? 'bg-red-100 text-red-700 border-red-300 shadow-sm'
                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                              }`}
                            >
                              <XCircle className="h-4 w-4" />
                            </button>

                            {/* Excused Button */}
                            <button
                              onClick={() => handleAttendanceChange(record.studentId, AttendanceStatus.ABSENT_EXCUSED)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                                currentStatus === 'ABSENT_EXCUSED'
                                  ? 'bg-blue-100 text-blue-700 border-blue-300 shadow-sm'
                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200'
                              }`}
                            >
                              <AlertCircle className="h-4 w-4" />
                            </button>

                            {/* Late Button with Dropdown */}
                            <div className="relative">
                              <button
                                onClick={() => {
                                  if (currentStatus === 'LATE') {
                                    setShowLateInput(showLateInput === record.studentId ? null : record.studentId);
                                  } else {
                                    handleAttendanceChange(record.studentId, AttendanceStatus.LATE, 15);
                                  }
                                }}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border flex items-center gap-1 ${
                                  currentStatus === 'LATE'
                                    ? 'bg-amber-100 text-amber-700 border-amber-300 shadow-sm'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200'
                                }`}
                              >
                                <Clock className="h-4 w-4" />
                                {currentStatus === 'LATE' && (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </button>

                              {/* Late Minutes Input Dropdown */}
                              {showLateInput === record.studentId && currentStatus === 'LATE' && (
                                <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-20">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Minutes de retard
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      min="1"
                                      max="180"
                                      value={currentLateMinutes}
                                      onChange={(e) => handleAttendanceChange(
                                        record.studentId,
                                        AttendanceStatus.LATE,
                                        parseInt(e.target.value) || 15
                                      )}
                                      className="w-20 px-2 py-1.5 text-center border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    />
                                    <span className="text-sm text-gray-500">min</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-gray-400">
                            <Lock className="h-4 w-4" />
                            <span className="text-sm">Lecture seule</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Empty Search Results */}
          {sessionData && filteredRecords.length === 0 && searchQuery && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Search className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-lg font-medium text-gray-600">Aucun résultat</p>
              <p className="text-sm text-gray-400 mt-1">
                Aucun étudiant ne correspond à "{searchQuery}"
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {hasChanges ? (
              <span className="text-blue-600 font-medium">
                {Object.keys(attendanceEdits).length} modification(s) non enregistrée(s)
              </span>
            ) : (
              <span>Aucune modification</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors font-medium"
            >
              {hasChanges ? 'Annuler' : 'Fermer'}
            </button>

            {session.canEdit && (
              <button
                onClick={handleSaveClick}
                disabled={isSaving || !hasChanges}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Enregistrer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Incomplete Attendance Warning Modal */}
      {showIncompleteWarning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Warning Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-amber-500 to-orange-500">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    Feuille incomplète
                  </h3>
                  <p className="text-amber-100 text-sm">
                    Attention, des étudiants ne sont pas marqués
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
                  <UserX className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <p className="text-gray-700 font-medium mb-1">
                    {getUnmarkedCount()} étudiant(s) non marqué(s)
                  </p>
                  <p className="text-gray-500 text-sm">
                    Voulez-vous marquer les étudiants restants comme absents et enregistrer la feuille de présence ?
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleMarkRemainingAbsentAndSave}
                  disabled={isSaving}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all font-medium shadow-sm disabled:opacity-50"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  Marquer absents et enregistrer
                </button>
                <button
                  onClick={() => setShowIncompleteWarning(false)}
                  disabled={isSaving}
                  className="w-full px-5 py-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors font-medium"
                >
                  Retourner à la feuille
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceModal;
